import { motion } from 'framer-motion'
import { useUserStore } from '@/stores/useUserStore'
import { useCharacterStore } from '@/stores/useCharacterStore'
import { useSettingsStore } from '@/stores/useSettingsStore'
import { XpBar } from '@/components/ui'
import { CHARACTERS } from '@/types'
import { Heart } from 'lucide-react'
import { CharacterSprite, type AnimationState } from './CharacterSprite'

interface CharacterDisplayProps {
  showStats?: boolean
  size?: 'sm' | 'md' | 'lg'
  animationState?: AnimationState
  expression?: string
  onCharacterClick?: () => void
}

const spriteSize = {
  sm: 'sm' as const,
  md: 'md' as const,
  lg: 'lg' as const,
}

const containerHeight = {
  sm: 'h-40',
  md: 'h-64',
  lg: 'h-80',
}

export function CharacterDisplay({
  showStats = true,
  size = 'md',
  animationState = 'idle',
  expression,
  onCharacterClick,
}: CharacterDisplayProps) {
  const { activeCharacterId, xpInCurrentLevel, xpForNextLevel, level } = useUserStore()
  const { getAffectionLevel } = useCharacterStore()
  const { contentLevel } = useSettingsStore()

  const characterData = CHARACTERS[activeCharacterId]
  const affectionLevel = getAffectionLevel(activeCharacterId)

  return (
    <div className="bg-gradient-to-b from-bg-surface to-bg-dark rounded-3xl p-4 relative overflow-hidden">
      {/* Character display area */}
      <div className={`${containerHeight[size]} flex items-center justify-center relative mb-4`}>
        <CharacterSprite
          characterId={activeCharacterId}
          contentLevel={contentLevel}
          animationState={animationState}
          expression={expression}
          spriteType="portrait"
          size={spriteSize[size]}
          showParticles={animationState === 'celebration'}
          particleType={activeCharacterId === 'yuki' ? 'snow' : activeCharacterId === 'momo' ? 'petals' : activeCharacterId === 'sumire' ? 'stars' : 'sparkle'}
          onTap={onCharacterClick}
        />

        {/* Character name badge */}
        <motion.div
          className="absolute bottom-2 left-1/2 -translate-x-1/2 px-4 py-1.5 bg-primary/20 backdrop-blur-sm rounded-full border border-primary/30"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <span className="font-bold text-sm">{characterData.name}</span>
        </motion.div>
      </div>

      {/* Stats section */}
      {showStats && (
        <div className="space-y-4">
          <XpBar
            currentXp={xpInCurrentLevel}
            maxXp={xpForNextLevel}
            level={level}
            showMilestones
          />

          <div className="flex items-center gap-2">
            <span className="text-sm text-text-secondary">Affection</span>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((heartLevel) => (
                <motion.div
                  key={heartLevel}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: heartLevel * 0.1 }}
                >
                  <Heart
                    size={18}
                    className={`transition-colors ${
                      heartLevel <= affectionLevel
                        ? 'fill-red-400 text-red-400 animate-heartbeat'
                        : 'text-bg-dark'
                    }`}
                    style={{ animationDelay: `${heartLevel * 0.2}s` }}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
